/**
 * Browser-side calls to the auth API.
 *
 * Every request sends `credentials: 'include'`, because the session is
 * an httpOnly cookie the script cannot see — which is the point. There
 * is no token to store, attach, or accidentally log.
 */

export interface PublicAccount {
  id: number;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
  locale: string;
  hasPassword: boolean;
}

export interface Preferences {
  locale: string;
  theme: 'system' | 'light' | 'dark';
  categories: string[];
  companies: string[];
  products: string[];
  wantsDigest: boolean;
  wantsInstant: boolean;
  instantThreshold: number;
}

function apiBase(): string {
  return (process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:4000/api').replace(/\/+$/, '');
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });

  // The API answers with { ok: false, error } for expected failures, so
  // a non-2xx here is genuinely unexpected.
  if (!response.ok && response.status !== 401) {
    throw new Error(`Request failed (${response.status})`);
  }

  return (await response.json()) as T;
}

export const register = (body: {
  email: string;
  password: string;
  locale?: string;
}) => call<{ ok: boolean; error?: string }>('/auth/register', {
  method: 'POST',
  body: JSON.stringify(body),
});

export const login = (body: { email: string; password: string }) =>
  call<{ ok: boolean; error?: string; needsVerification?: boolean }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const logout = () => call<{ ok: boolean }>('/auth/logout', { method: 'POST' });

export const deleteAccount = () =>
  call<{ ok: boolean; error?: string }>('/auth/account', {
    method: 'DELETE',
    body: JSON.stringify({ confirm: true }),
  });

export const me = () => call<{ account: PublicAccount | null }>('/auth/me');

export const getPreferences = () =>
  call<{ preferences: Preferences }>('/account/preferences');

export const savePreferences = (body: Partial<Preferences>) =>
  call<{ preferences: Preferences }>('/account/preferences', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const getChannels = () =>
  call<{
    email: Array<{ id: number; address: string | null; status: string }>;
    telegram: Array<{ id: number; username: string | null; status: string }>;
    devices: Array<{ id: number; platform: string; status: string }>;
  }>('/account/channels');

export const getSessions = () =>
  call<{
    sessions: Array<{
      id: number;
      client: string;
      userAgent: string | null;
      lastUsedAt: string | null;
      current: boolean;
    }>;
  }>('/auth/sessions');

export const revokeOtherSessions = () =>
  call<{ ok: boolean; revoked: number }>('/auth/sessions/revoke-others', { method: 'POST' });

export const getLinkedLogins = () =>
  call<{ linked: Array<{ provider: string; email: string | null }> }>('/auth/oauth/linked');

export const getOAuthProviders = () =>
  call<{ providers: string[] }>('/auth/oauth/providers');

export const changePassword = (body: { currentPassword: string; newPassword: string }) =>
  call<{ ok: boolean; error?: string }>('/auth/password', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** Where the browser goes to start a social login. */
export function oauthUrl(provider: string, link = false): string {
  return `${apiBase()}/auth/oauth/${provider}${link ? '?link=1' : ''}`;
}
